import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { resourceBySlug, RESOURCES } from '@/data/seo/resources';
import { SEO_TRADES } from '@/data/seo/trades';
import { breadcrumbSchema, faqSchema, articleSchema, platformFaqs } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, PageWrap,
} from '@/components/seo/SeoBlocks';

const ResourcePage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const res = resourceBySlug(slug);
  if (!res) return <NotFound />;

  const title = lang === 'es' ? res.titleEs : res.title;
  const answer = lang === 'es' ? res.answerEs : res.answer;
  const url = `/${lang}/resources/${res.slug}`;
  const faqs = platformFaqs(lang);

  const related = RESOURCES.filter((r) => r.topic === res.topic && r.slug !== res.slug)
    .slice(0, 10)
    .map((r) => ({ label: lang === 'es' ? r.titleEs : r.title, to: path(`/resources/${r.slug}`) }));

  const tradeMatch = SEO_TRADES.find((x) => res.slug === `how-to-bid-${x.slug}-jobs`);
  const tradeLinks = (tradeMatch ? [tradeMatch, ...SEO_TRADES.filter((x) => x.group === tradeMatch.group && x.slug !== tradeMatch.slug)] : SEO_TRADES)
    .slice(0, 12)
    .map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }));

  return (
    <>
      <Seo
        title={title}
        description={answer.slice(0, 180)}
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'Resource Center', url: `/${lang}/resources` },
            { name: res.title, url },
          ]),
          articleSchema({ headline: res.title, description: res.answer, url, topic: res.topic }),
          faqSchema(faqs),
        ]}
      />

      <PageHero eyebrow={t('seo.resourceEyebrow')} title={title} intro={answer} />

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: t('seo.resourcesTitle'), url: path('/resources') },
            { name: title },
          ]}
        />

        <AnswerBlock question={title} answer={answer} />

        <article className="mt-2">
          {res.sections.map((sec) => (
            <Section key={sec.h} title={sec.h}>
              <p>{sec.p}</p>
            </Section>
          ))}
        </article>

        <Section title={t('seo.howGigGoneHelps')}>
          <p>{t('seo.resourceHelpBody')}</p>
          <div className="flex flex-wrap gap-3">
            <Link to={path('/jobs')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600">
              {t('seo.ctaBrowse')}
            </Link>
            <Link to={path('/register')} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50">
              {t('seo.ctaSubBtn')}
            </Link>
          </div>
        </Section>

        <FaqSection faqs={faqs} />

        <LinkGrid title={t('seo.relatedGuides')} links={related} columns={2} />
        <LinkGrid title={t('seo.relatedTrades')} links={tradeLinks} icon="trade" />

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default ResourcePage;
